import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Portal, Snackbar } from '@mui/material';
import { shouldSuppressSessionToast } from '../utils/session';

/**
 * Globális visszajelzés (snackbar) minden sikeres és hibás művelethez.
 *
 * MIÉRT: a webes UX-elemzés (6.3) négyféle, egymás mellett élő mintát talált — magától eltűnő
 * Alert, a lap tetején maradó sáv (hosszú űrlapon a látómezőn kívül), SEMMILYEN visszajelzés
 * (7+ képernyő: mentés után csak becsukódik a dialógus), és dialóguson belüli „Bezárás" gomb,
 * ami funkcionálisan OK-ablak. A user-szabály: siker után NINCS OK-ablak, csak toast.
 * Ez a provider az egyetlen, egységes hely: alul középen felbukkanó rövid csík, ami magától eltűnik.
 */

type FeedbackSeverity = 'success' | 'error' | 'info' | 'warning';

interface FeedbackMessage {
  key: number;
  text: string;
  severity: FeedbackSeverity;
}

interface FeedbackContextValue {
  /** Sikeres művelet visszajelzése (rövid, magától eltűnik). */
  showSuccess: (text: string) => void;
  /** Hiba visszajelzése (hosszabb ideig látszik, kézzel is zárható). */
  showError: (text: string) => void;
  /** Semleges tájékoztatás. */
  showInfo: (text: string) => void;
  /** Figyelmeztetés (a hibánál rövidebb ideig látszik). */
  showWarning: (text: string) => void;
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

/**
 * ══ A TOAST NE ESSEN A KÉPERNYŐ ALJÁN RÖGZÍTETT SÁVOKRA [Web UX 9 / T3, 2026-10-13] ══
 *
 * MI VOLT A BAJ (2. körös UX-lista (`Documents/WEB_UX_JAVASLATOK_2KOR_2026-10-04.md`) 2.3 és 5.5): a toast fixen `bottom: 24`-nél (telefonon 8-nál)
 * jelent meg (lent, a `Snackbar` `anchorOrigin`-ja), így PONTOSAN a jelenléti ív rögzített
 * mentés-sávjára (`EventAttendance.tsx` `renderSaveBar`) esett — a „Jelenlét mentve." épp a
 * Mentés gomb fölé ült —, telefonon pedig az alsó menüt (`MobileBottomNav`) takarta.
 *
 * A MEGOLDÁS: a képernyő alján ülő elemek „akadályként" bejelentkeznek (`useToastObstacle`), és a
 * toast a MEGJELENÉSE pillanatában a legmagasabbra nyúló akadály TETEJE fölé kerül. Mért érték,
 * nem bűvös szám: a menü magassága a safe-area-tól és a felirat-tördeléstől is függ, a sávé a
 * gomb méretétől — egy konstans ezeket nem követné. Akadály nélkül (a legtöbb oldal asztalon)
 * a toast helye VÁLTOZATLAN (a MUI alapértéke).
 */
type ToastObstacleRegister = (el: HTMLElement) => () => void;
const ToastObstacleContext = createContext<ToastObstacleRegister | null>(null);

/** A toast és az akadály teteje közti rés (px) — a MUI telefonos alapértéke (`bottom: 8`). */
export const TOAST_OBSTACLE_GAP_PX = 8;

/**
 * A toast alsó távolsága a nézet aljától (px), vagy `null`, ha nincs látható akadály.
 * Kizárólag a nézetben LÁTHATÓ, valódi méretű akadály számít (jsdom-ban minden téglalap 0 → `null`,
 * vagyis a régi viselkedés).
 */
export function toastBottomOffset(obstacles: Iterable<HTMLElement>, viewportHeight: number): number | null {
  let maxReach: number | null = null;
  for (const el of Array.from(obstacles)) {
    const r = el.getBoundingClientRect();
    if (r.height <= 0 || r.width <= 0) continue;
    if (r.top >= viewportHeight || r.bottom <= 0) continue; // a nézeten kívül
    const reach = viewportHeight - r.top;
    if (maxReach === null || reach > maxReach) maxReach = reach;
  }
  return maxReach === null ? null : Math.ceil(maxReach) + TOAST_OBSTACLE_GAP_PX;
}

/**
 * Callback-ref: a vele jelölt elem (amíg a DOM-ban van) a toast „akadálya" — a toast fölé kerül.
 * Provider nélkül (régi tesztek) semmit nem csinál.
 */
export function useToastObstacle(): (el: HTMLElement | null) => void {
  const register = useContext(ToastObstacleContext);
  const unregisterRef = useRef<(() => void) | null>(null);
  return useCallback(
    (el: HTMLElement | null) => {
      unregisterRef.current?.();
      unregisterRef.current = el && register ? register(el) : null;
    },
    [register],
  );
}

const AUTO_HIDE_MS: Record<FeedbackSeverity, number | null> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 8000,
};

export const FeedbackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<FeedbackMessage[]>([]);
  const [current, setCurrent] = useState<FeedbackMessage | null>(null);
  const [open, setOpen] = useState(false);

  /**
   * HÁNY ÜZENET VÁRAKOZOTT MÁR, AMIKOR A MOSTANIT MEGJELENÍTETTÜK [B-W2, 2026-09-16].
   *
   * Ez a szám dönti el, hogy egy üzenetet szabad-e idő előtt visszazárni — lásd a lenti effekt
   * indoklását. Szándékosan `ref`, nem állapot: nem a megjelenítést befolyásolja, hanem egy
   * döntést, állapotként pedig egy újabb render-kört (és újabb versenyt) hozna be.
   */
  const queuedWhenShownRef = useRef(0);

  /** A képernyő alján rögzített elemek (mentés-sáv, alsó menü) [Web UX 9 / T3, 2026-10-13]. */
  const obstaclesRef = useRef<Set<HTMLElement>>(new Set());
  /** A MOSTANI toast alsó távolsága — a megjelenéskor mérve; `null` = a MUI alapértéke. */
  const [toastBottom, setToastBottom] = useState<number | null>(null);
  /**
   * Látszik-e MOST toast (a megjelenéstől a kilépő animáció végéig) [Web UX 9 / T3, 2026-10-13, validátor F2].
   * MI VOLT A BAJ: a `bottom` CSAK a megjelenéskor számolódott — ha a sáv közben eltűnt (mentés után
   * azonnal fülváltás), a toast a régi helyén, 69 px-rel a menü fölött lebegett 4 mp-ig (MÉRVE, 390 px).
   * Most nyitott toast mellett az akadályok be-/kijelentkezése, átméreteződése és az ablak
   * átméretezése is újraszámolja. Őr: `__tests__/FeedbackContext.obstacle.test.tsx` „F2" esetei.
   */
  const toastShownRef = useRef(false);
  const recomputeToastBottom = useCallback(() => {
    if (!toastShownRef.current) return;
    setToastBottom(toastBottomOffset(obstaclesRef.current, window.innerHeight));
  }, []);
  const obstacleResizeObserverRef = useRef<ResizeObserver | null>(null);
  const registerObstacle = useCallback<ToastObstacleRegister>((el) => {
    obstaclesRef.current.add(el);
    if (!obstacleResizeObserverRef.current && typeof ResizeObserver !== 'undefined') {
      obstacleResizeObserverRef.current = new ResizeObserver(() => recomputeToastBottom());
    }
    obstacleResizeObserverRef.current?.observe(el);
    recomputeToastBottom();
    return () => {
      obstaclesRef.current.delete(el);
      obstacleResizeObserverRef.current?.unobserve(el);
      recomputeToastBottom();
    };
  }, [recomputeToastBottom]);
  useEffect(() => {
    if (current === null) return undefined;
    window.addEventListener('resize', recomputeToastBottom);
    return () => window.removeEventListener('resize', recomputeToastBottom);
  }, [current, recomputeToastBottom]);
  useEffect(() => () => obstacleResizeObserverRef.current?.disconnect(), []);

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════
   * EGY ÜZENETET CSAK ÚJ FELHASZNÁLÓI MŰVELET SZORÍTHAT KI — A SAJÁT PÁRJA NEM
   * [B-W2, 2026-09-16]
   * ══════════════════════════════════════════════════════════════════════════════════════════
   *
   * A minta MUI „egymás utáni snackbarok": egyszerre egy látszik, a következő az előző
   * becsukódása után jön. A második ág („szoríts helyet a következőnek") szándéka az, hogy ha a
   * felhasználó KÖZBEN újabb műveletet végez, ne kelljen végigvárnia az előző visszajelzést.
   *
   * ══ MI VOLT A BAJ (MÉRVE 2026-09-16, jsdom-teszttel) ══
   * A feltétel csak annyi volt, hogy „van még a sorban". Amikor EGY kezelő KÉT üzenetet küldött
   * (a gyakori „siker + figyelmeztetés" pár), a sor egy tickben `[A, B]` lett. Az effekt ELSŐ
   * lefutása kivette A-t (`current = A`, `queue = [B]`, `open = true`), a MÁSODIK lefutása
   * viszont AZONNAL teljesült ezen az ágon, és VISSZAZÁRTA A-t — mielőtt bárki elolvashatta
   * volna. A mérés (A-nak 4000 ms-ig kellene látszania):
   *   T=0 ms    → A a DOM-ban, B nincs
   *   T=600 ms  → A MÁR NINCS a DOM-ban, B látszik     ← A ~200 ms-ig „villant"
   * Vagyis a két mondat közül a felhasználó csak az egyiket olvashatta el.
   * ⚠️ Jsdom-ban a szöveg a kilépő animáció alatt még a DOM-ban van, ezért egy „megjelenik-e"
   * típusú teszt a HIBÁS kódon is ZÖLD volt (mérve) — az őr-teszt
   * (`__tests__/FeedbackContext.queue.test.tsx`) ezért IDŐT mér, nem puszta jelenlétet.
   *
   * ══ A JAVÍTÁS: OKOZATI, NEM IDŐALAPÚ FELTÉTEL ══
   * A kiszorítás CSAK akkor jogos, ha az új üzenet a mostani MEGJELENÍTÉSE UTÁN keletkezett
   * (tehát ÚJ felhasználói művelet hozta). Ezt a `queuedWhenShownRef` mondja meg: a megjelenítés
   * pillanatában feljegyezzük, mennyi MARADT a sorban; ha a sor ENNÉL HOSSZABB lesz, akkor jött
   * valami ÚJ — és csak akkor zárunk.
   *   · egy kezelő két üzenete  → mindkettő MÁR a sorban volt → NINCS kiszorítás: A kivárja a
   *     maga idejét, aztán jön B;
   *   · két külön felhasználói művelet → az új üzenet a sor NÖVEKEDÉSEKÉNT látszik → a régi
   *     visszajelzés kiszorul, ahogy eddig is (a felületen ez a megszokott, gyors válasz).
   *
   * ⚠️ MIÉRT NEM IDŐKORLÁT („legalább 1,5 s-ig látszódjon"): azzal a két eset összemosódik, és a
   * sorozatos műveletek visszajelzése LASSÚVÁ válik. MÉRVE 2026-09-16: a „mindenki megkapja a
   * teljes idejét" változattal HÁROM meglévő teszt bukott el, mert a második toast csak ~4 s
   * múlva jött (jelenléti ív mentése, résztvevő-eltávolítás, pénz-vázlat blokkolás). Az okozati
   * feltétel mindkét esetet helyesen kezeli, és nem hoz be új időzítést (tehát flaky-t) sem.
   */
  useEffect(() => {
    if (queue.length > 0 && current === null) {
      // FONTOS: a feljegyzés a COMMITOLT sorból megy (`queue`), nem a `setQueue` frissítőjéből —
      // az updater-nek TISZTÁNAK kell lennie (React StrictMode kétszer is meghívhatja).
      queuedWhenShownRef.current = queue.length - 1;
      // [Web UX 9 / T3] a megjelenés pillanatában mérjük, mi ül a képernyő alján (lásd fent).
      toastShownRef.current = true;
      setToastBottom(toastBottomOffset(obstaclesRef.current, window.innerHeight));
      setCurrent(queue[0]);
      setQueue((rest) => rest.slice(1));
      setOpen(true);
    } else if (current !== null && open && queue.length > queuedWhenShownRef.current) {
      setOpen(false);
    }
  }, [queue, current, open]);

  const push = useCallback((text: string, severity: FeedbackSeverity) => {
    if (!text) return;
    setQueue((rest) => [...rest, { key: Date.now() + Math.random(), text, severity }]);
  }, []);

  const value = useMemo<FeedbackContextValue>(
    () => ({
      showSuccess: (text: string) => push(text, 'success'),
      showError: (text: string) => {
        // [7. javító kör — validátor F2, 2026-10-04] Ablak nélkül lejárt munkamenetnél a belépő
        // oldal maga mondja ki a „Lejárt a bejelentkezésed…" mondatot — a túlélő toast ne duplázza
        // (lásd `utils/session.ts` `shouldSuppressSessionToast`).
        if (shouldSuppressSessionToast(text)) return;
        push(text, 'error');
      },
      showInfo: (text: string) => push(text, 'info'),
      showWarning: (text: string) => push(text, 'warning'),
    }),
    [push]
  );

  const handleClose = (_event?: unknown, reason?: string) => {
    // A kattintás a lapon ne tüntesse el a visszajelzést idő előtt.
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  return (
    <FeedbackContext.Provider value={value}>
      <ToastObstacleContext.Provider value={registerObstacle}>
      {children}
      </ToastObstacleContext.Provider>
      {/* Portal: nyitott MUI Dialog mellett a Modal a body AKKOR MEGLÉVŐ gyerekeit `aria-hidden`-re
          állítja. A React-gyökéren belül renderelő Snackbart ez elrejtené a képernyőolvasó elől
          (vizuálisan látszana: zIndex 1400 a modal 1300-a fölött). Portálban a zárt Snackbar semmit
          nem renderel, ezért a csomópontja a modal regisztrációja UTÁN kerül a body-ba → nem kap
          aria-hiddent. ⚠️ KÖVETKEZMÉNY: ezért NE adj a Snackbarnak `keepMounted`-et vagy mindig
          renderelt tartalmat, mert azzal ez a védelem csendben visszaromlana. Ismert korlát: ha a
          toast MÁR látszik és utána nyílik egy dialógus, a toast kiesik az akadálymentességi fából
          (vizuálisan megmarad) — ezt a MUI Modal viselkedése okozza. */}
      <Portal>
      <Snackbar
        key={current?.key}
        open={open}
        autoHideDuration={current ? AUTO_HIDE_MS[current.severity] : null}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        // [Web UX 9 / T3] inline stílus: a MUI töréspontos `bottom` szabályait (8 / 24 px) is felülírja.
        style={toastBottom !== null ? { bottom: toastBottom } : undefined}
        // A KILÉPŐ ANIMÁCIÓ VÉGE szabadítja fel a helyet a következő üzenetnek. Amíg
        // `current !== null`, az effekt nem tud újat előhúzni [B-W2, 2026-09-16].
        TransitionProps={{ onExited: () => { toastShownRef.current = false; setCurrent(null); } }}
        data-testid="feedback-snackbar"
      >
        <Alert
          onClose={() => setOpen(false)}
          severity={current?.severity ?? 'success'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {current?.text ?? ''}
        </Alert>
      </Snackbar>
      </Portal>
    </FeedbackContext.Provider>
  );
};

/**
 * Visszajelzés küldése bárhonnan. Provider nélkül (pl. régi teszt) nem dob, hanem csendben
 * elnyeli a hívást: egy elmaradó toast nem indokolja egy működő képernyő összeomlását.
 */
export function useFeedback(): FeedbackContextValue {
  const ctx = useContext(FeedbackContext);
  return ctx ?? NOOP_FEEDBACK;
}

const NOOP_FEEDBACK: FeedbackContextValue = {
  showSuccess: () => {},
  showError: () => {},
  showInfo: () => {},
  showWarning: () => {},
};

export default FeedbackProvider;
